import React, { useMemo } from "react";
import { Col, Dropdown, Form, Row } from "react-bootstrap";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  useWatch,
} from "react-hook-form";
import { Section } from "../../components/section";
import { ProgramInputs } from "../../schemas/inputs";
import { availableEvaluationTypes } from "../../support/availableEvaluationTypes";
import { EvaluationStageCriteria } from "./EvaluationStageCriteria";
import { EvaluationStageSections } from "./EvaluationStageSections";
import { EvaluationTypeContainer, EvaluationTypeToggle } from "./EvaluationFormStage.style";

interface EvaluationFormStageProps {
  stageIndex: number;
  totalStages: number;
  control: Control<ProgramInputs>;
  register: UseFormRegister<ProgramInputs>;
  errors: FieldErrors<ProgramInputs>;
  onDelete: () => void;
  onDuplicate: () => void;
}

export const EvaluationFormStage = ({
  stageIndex,
  totalStages,
  control,
  register,
  errors,
  onDelete,
  onDuplicate,
}: EvaluationFormStageProps) => {

  const savedEvaluationType = useWatch({
    control,
    name: `evaluations.${stageIndex}.type`,
  });

  const currentEvaluationType = useMemo(() => {
    if (!savedEvaluationType) {
      return null;
    }

    return availableEvaluationTypes.find(
      item => item.value === savedEvaluationType.value
    ) ?? availableEvaluationTypes[0];

  }, [savedEvaluationType, availableEvaluationTypes]);

  return (
    <Section
      isOpen={true}
      label={`Etapa ${stageIndex + 1}`}
      hasBorder={true}
      hasMarginTop={true}
      showActions={totalStages > 1}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      isFixed={true}
    >
      <Row className="g-3">
        <Col xs={12} md={6}>
          <Form.Group controlId={`evaluations.${stageIndex}.name`}>
            <Form.Label required>Nome da etapa</Form.Label>
            <Form.Control
              placeholder="Digite o nome da etapa"
              {...register(`evaluations.${stageIndex}.name`)}
              isInvalid={!!errors.evaluations?.[stageIndex]?.name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.evaluations?.[stageIndex]?.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex gap-3 align-items-end flex-wrap">
            <EvaluationTypeContainer>
              <Form.Group controlId={`evaluations.${stageIndex}.type`}>
                <Form.Label required>Tipo de avaliação</Form.Label>
                <Controller
                  control={control}
                  name={`evaluations.${stageIndex}.type`}
                  render={({ field }) => (
                    <Dropdown
                      className={`w-100 ${errors.evaluations?.[stageIndex]?.type
                          ? "border-danger"
                          : ""
                        }`}
                      onSelect={(eventKey: string | null) => {
                        if (eventKey) {
                          const selectedType = availableEvaluationTypes.find(
                            (t) => t.value === eventKey,
                          );
                          if (selectedType) {
                            field.onChange(selectedType);
                          }
                        }
                      }}
                    >
                      <EvaluationTypeToggle
                        variant="transparent"
                        className={`w-100 d-flex align-items-center justify-content-between text-start ${errors.evaluations?.[stageIndex]?.type
                            ? "border-danger"
                            : ""
                          }`}
                      >
                        {field.value ? field.value.label : "Selecione o tipo"}
                      </EvaluationTypeToggle>
                      <Dropdown.Menu className="w-100">
                        {availableEvaluationTypes.map((type) => (
                          <Dropdown.Item key={type.value} eventKey={type.value}>
                            {type.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                />
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.evaluations?.[stageIndex]?.type?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </EvaluationTypeContainer>
            {currentEvaluationType?.allowWeigth && (
              <div className="w-auto">
                <Form.Group
                  controlId={`evaluations.${stageIndex}.weightPercent`}
                >
                  <Form.Label required>% Peso</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.1"
                    placeholder="Informe o peso em percentual"
                    {...register(`evaluations.${stageIndex}.weightPercent`)}
                    isInvalid={
                      !!errors.evaluations?.[stageIndex]?.weightPercent
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {/* {errors.evaluations?.[stageIndex]?.weightPercent?.message} */}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row className="g-3">
        <Col xs={12} md={6}>
          <Form.Group controlId={`evaluations.${stageIndex}.startDate`}>
            <Form.Label required>Início</Form.Label>
            <Form.Control
              type="date"
              {...register(`evaluations.${stageIndex}.startDate`)}
              isInvalid={!!errors.evaluations?.[stageIndex]?.startDate}
            />
            <Form.Control.Feedback type="invalid">
              {errors.evaluations?.[stageIndex]?.startDate?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Form.Group controlId={`evaluations.${stageIndex}.endDate`}>
            <Form.Label required>Fim</Form.Label>
            <Form.Control
              type="date"
              {...register(`evaluations.${stageIndex}.endDate`)}
              isInvalid={!!errors.evaluations?.[stageIndex]?.endDate}
            />
            <Form.Control.Feedback type="invalid">
              {errors.evaluations?.[stageIndex]?.endDate?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {currentEvaluationType !== null && !currentEvaluationType.allowMultSection && (
        <EvaluationStageCriteria
          stageIndex={stageIndex}
          control={control}
          register={register}
          errors={errors}
        />
      )}

      {currentEvaluationType !== null && currentEvaluationType.allowMultSection && (
        <EvaluationStageSections
          stageIndex={stageIndex}
          control={control}
          register={register}
          errors={errors}
          evaluationType={currentEvaluationType}
        />
      )}
    </Section>
  );
};
